import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Select, FormControl, InputLabel,
  Grid, Typography, Divider, Box, CircularProgress, Alert, Switch, FormControlLabel,
} from '@mui/material';
import { Add, SportsScore } from '@mui/icons-material';

const CATEGORIES = ['group_stage', 'round_of_16', 'quarter_final', 'semi_final', 'final', 'league', 'friendly'];

export const AddGameEntryDialog = ({ open, onClose, stage, event, onCreate, createGame }) => {
  const [createReal, setCreateReal] = useState(false);
  const [entity1Id, setEntity1Id] = useState('');
  const [entity2Id, setEntity2Id] = useState('');
  const [position, setPosition] = useState(stage?.games?.length ?? 0);
  // real game fields
  const [gameForm, setGameForm] = useState({
    categorie: '', gameDate: '', startTime: '',
    venueName: '', venueCity: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const participants = event?.entitiesParticipants || [];
  const games = event?.games || [];

  const handleClose = () => {
    setEntity1Id(''); setEntity2Id(''); setCreateReal(false);
    setGameForm({ categorie: '', gameDate: '', startTime: '', venueName: '', venueCity: '' });
    setError(''); onClose();
  };

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try {
      let gameId = null;
      if (createReal) {
        if (!entity1Id || !entity2Id || !gameForm.gameDate) {
          setError('Entity 1, Entity 2, and Game Date are required to create a real game.');
          setLoading(false); return;
        }
        const created = await createGame({
          eventId: event._id,
          categorie: gameForm.categorie || undefined,
          entity1: entity1Id,
          entity2: entity2Id,
          gameDate: gameForm.gameDate,
          startTime: gameForm.startTime || undefined,
          venue: { name: gameForm.venueName, city: gameForm.venueCity },
        });
        gameId = created?._id;
        // Use entity ids from the new game
        await onCreate({ entity1Id: entity1Id || null, entity2Id: entity2Id || null, gameId, position: Number(position) });
      } else {
        await onCreate({ entity1Id: entity1Id || null, entity2Id: entity2Id || null, gameId: null, position: Number(position) });
      }
      handleClose();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

 return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SportsScore color="primary" />
        <Typography fontWeight="bold">Ajouter un match — {stage?.title}</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Équipe 1 (optionnel)</InputLabel>
              <Select value={entity1Id} label="Équipe 1 (optionnel)" onChange={e => setEntity1Id(e.target.value)}>
                <MenuItem value=""><em>À définir</em></MenuItem>
                {participants.map(p => {
                  const id = p?._id || p; const name = p?.name || id;
                  return <MenuItem key={id} value={id}>{name}</MenuItem>;
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Équipe 2 (optionnel)</InputLabel>
              <Select value={entity2Id} label="Équipe 2 (optionnel)" onChange={e => setEntity2Id(e.target.value)}>
                <MenuItem value=""><em>À définir</em></MenuItem>
                {participants.map(p => {
                  const id = p?._id || p; const name = p?.name || id;
                  return <MenuItem key={id} value={id}>{name}</MenuItem>;
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Position (base 0)" type="number"
              value={position} onChange={e => setPosition(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={createReal} onChange={e => setCreateReal(e.target.checked)} />}
              label="Créer également un vrai enregistrement de match" />
          </Grid>
          {createReal && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Catégorie</InputLabel>
                  <Select value={gameForm.categorie} label="Catégorie"
                    onChange={e => setGameForm(f => ({ ...f, categorie: e.target.value }))}>
                    {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Date du match" type="datetime-local"
                  InputLabelProps={{ shrink: true }} value={gameForm.gameDate}
                  onChange={e => setGameForm(f => ({ ...f, gameDate: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Heure de début (HH:mm)" value={gameForm.startTime}
                  onChange={e => setGameForm(f => ({ ...f, startTime: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Nom du lieu" value={gameForm.venueName}
                  onChange={e => setGameForm(f => ({ ...f, venueName: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Ville du lieu" value={gameForm.venueCity}
                  onChange={e => setGameForm(f => ({ ...f, venueCity: e.target.value }))} />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={handleClose} variant="outlined">Annuler</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Add />}>
          Ajouter un match
        </Button>
      </DialogActions>
    </Dialog>
  );
};
